import {
    Box, Grid, Tabs, Typography
} from '@mui/material';
import { CancelButtonGrey } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { APIConnectionRecord } from '@tassat/fred-definitions';
import { Lock as LockIcon } from '@tassat/icon';
import { TabPanel } from '@tassat/layout';
import { StyledTab } from '@tassat/menu';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import history from 'Lib/browserHistory';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAPIConnectionById } from 'Selectors/apiConnections';
import ActionsDropdownMenu from 'SharedComponents/actionsDropdownMenu/actionsDropdownMenu';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

import APIConnectionFirmsAgGrid from './apiFirmView';
import APIConnectionUsersAgGrid from './apiUserView';
import { ConnectionDetails } from './detailsView/connectionDetails';

import type { ApiConnectionDetailsProps } from '.';

function tabbingProps(index: any) {
    return {};
}
export function ApiConnectionDetails(props: ApiConnectionDetailsProps) {
    const {
        classes,
        editApiConnection
    } = props;
    const apiConnectionId = props.match.params.apiConnectionId;
    const apiConnection = useAppSelector((state) => getAPIConnectionById(state, apiConnectionId));
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const [features] = useFeatures();
    const isSecondaryApiConnection = features.fireblocks.api && apiConnection?.APISecondaryConnectionType?.length;

    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const [ currentTab, setCurrentTab ] = React.useState<number>(0);
    const deleteAPIConnectionValidation = (apiConnection: APIConnectionRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnection,
                actionType: ActionTypes.delete,
                confirmTitle: `Confirm Deleting Api Connection ${apiConnection.APIConnectionName}`,
                confirmBody: `Please confirm deleting the api connection ${apiConnection.APIConnectionName}`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const deleteAPIConnection = async (apiConnection: APIConnectionRecord) => {
        try {
            const sendValues = { ...apiConnection };

            const response = await Worker.getInstance()
                .deleteAPIConnection(sendValues.APIConnectionId);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Deleted Api Connection for ${apiConnection.APIConnectionName}`,
                        successBody: `Successfully deleted the api connection for ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
                history.push('/admin/apiConnections');
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete Api Connection for ${apiConnection.APIConnectionName}`,
                        failureBody: `Could not delete the api connection for ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Delete Api Connection for ${apiConnection.APIConnectionName}`,
                    failureBody: `Something went wrong deleting the api connection for ${apiConnection.APIConnectionName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const lockAPIConnectionValidation = (apiConnection: APIConnectionRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnection,
                actionType: apiConnection.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${apiConnection.IsLocked ? 'Unlocking' : 'Locking'} Api Connection ${apiConnection.APIConnectionName}`,
                confirmBody: `Please confirm ${apiConnection.IsLocked ? 'unlocking' : 'locking'} api connection functionality for ${apiConnection.APIConnectionName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockAPIConnection = async (apiConnection: APIConnectionRecord) => {
        try {
            const response = await Worker.getInstance()
                .updateAPIConnection({
                    IsLocked: !apiConnection.IsLocked,
                    APIConnectionId: apiConnection.APIConnectionId
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${apiConnection.IsLocked ? 'Unlocked' : 'Locked'} ${apiConnection.APIConnectionName}`,
                        successBody: `Successfully ${apiConnection.IsLocked ? 'unlocked' : 'locked'} api connection functionality for ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && response.data.Status === 49 || doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${apiConnection.IsLocked ? 'Unlock' : 'Lock'} ${apiConnection.APIConnectionName}`,
                        failureBody: `Could not ${apiConnection.IsLocked ? 'unlock' : 'lock'} api connection functionality for ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${apiConnection.IsLocked ? 'Unlock' : 'Lock'} ${apiConnection.APIConnectionName}`,
                    failureBody: `Something went wrong ${apiConnection.IsLocked ? 'unlocking' : 'locking'} api connection functionality for ${apiConnection.APIConnectionName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: apiConnection,
                setSelectedItem: () => apiConnection,
                preActionValidationAndConfig: {
                    [ActionTypes.lock]: lockAPIConnectionValidation,
                    [ActionTypes.unlock]: lockAPIConnectionValidation,
                    ...(!isSecondaryApiConnection && { [ActionTypes.delete]: deleteAPIConnectionValidation })
                },
                onConfirm: {
                    [ActionTypes.lock]: lockAPIConnection,
                    [ActionTypes.unlock]: lockAPIConnection,
                    ...(!isSecondaryApiConnection && { [ActionTypes.delete]: deleteAPIConnection })
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                justifyContent='center'
                spacing={0}
            >
                <Grid
                    className={classes.userContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        alignItems='flex-end'
                        className={classes.userHeaderContainer}
                        container
                        direction='row'
                        justifyContent='space-between'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Grid
                            container
                            item
                        >
                            <Typography
                                className={classes.apiTitle}
                                variant='h4'
                            >
                                <strong>{apiConnection.APIConnectionName}</strong>
                            </Typography>
                            {apiConnection.IsLocked &&
                                <LockIcon
                                    iconColor={LOCK_ICON_COLOR}
                                />}
                            <Box mt={1}>
                                <ActionsDropdownMenu
                                    data={apiConnection}
                                    locked={apiConnection.IsLocked}
                                />
                            </Box>
                        </Grid>
                        <Grid
                            className={classes.cancelButton}
                            item
                        >
                            <CancelButtonGrey
                                onClick={() => history.push('/admin/apiConnections')}
                            />
                        </Grid>
                    </Grid>
                    <Tabs
                        centered
                        classes={{ indicator: classes.indicator }}
                        className={classes.userTabsContainer}
                        onChange={(e, newTab) => setCurrentTab(newTab)}
                        value={currentTab}
                    >
                        {!isSecondaryApiConnection ?
                            <StyledTab
                                classes={{ root: classes.tabRoot }}
                                id='APIDetailsTab'
                                label='Details'
                                {...tabbingProps(0)}
                            /> : null}
                        <StyledTab
                            classes={{ root: classes.tabRoot }}
                            id='APIUsersTab'
                            label='API Users'
                            {...tabbingProps(1)}
                        />
                        <StyledTab
                            classes={{ root: classes.tabRoot }}
                            id='APIFirmsTab'
                            label='API Firms'
                            {...tabbingProps(2)}
                        />
                    </Tabs>
                    <Grid
                        item
                        xs={12}
                    >
                        {!isSecondaryApiConnection ?
                            <TabPanel
                                index={0}
                                value={currentTab}
                            >
                                <ConnectionDetails
                                    apiConnection={apiConnection}
                                    editApiConnection={editApiConnection}
                                />
                            </TabPanel> : null}
                        <TabPanel
                            index={isSecondaryApiConnection ? 0 : 1}
                            value={currentTab}
                        >
                            <APIConnectionUsersAgGrid
                                apiConnectionID={apiConnection.APIConnectionId}
                            />
                        </TabPanel>
                        <TabPanel
                            index={isSecondaryApiConnection ? 1 : 2}
                            value={currentTab}
                        >
                            <APIConnectionFirmsAgGrid
                                apiConnectionID={apiConnection.APIConnectionId}
                            />
                        </TabPanel>
                    </Grid>
                </Grid>
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}